import '.a{display:flex;}.ssr-plugin'
import '.a8{width:100%;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.e{text-align:center;}.ssr-plugin'
import '.t{padding:2rem;}.ssr-plugin'
import '.fe{transition:transform 0.2s ease, opacity 0.2s ease;}.ssr-plugin'
import '.ay{margin-bottom:2rem;}.ssr-plugin'
import '.h{justify-content:center;}.ssr-plugin'
import '.r{overflow:hidden;}.ssr-plugin'
import '.ff{margin-top:0.5rem;}.ssr-plugin'
import '.bq{background:none;}.ssr-plugin'
import '.b3{border:none;}.ssr-plugin'
import '.b8{cursor:pointer;}.ssr-plugin'
import '.aj{color:var(--primary-blue);}.ssr-plugin'
import '.fg{text-decoration:underline;}.ssr-plugin'
import '.br{font-weight:500;}.ssr-plugin'
import '.b5{padding:0px;}.ssr-plugin'
import '.fh{font-size:inherit;}.ssr-plugin'
import '.c6{max-width:100%;}.ssr-plugin'
import '.c0{flex-shrink:0;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import { Title } from '@app/components/core/typography/Title'
import { TextLink } from '@app/components/core/link/TextLink'
import { Button } from '@app/components/core/button/Button'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useState, useEffect } from 'react'
import { useLocation } from 'wouter'
import { Image } from '@app/components/core/image/Image'

type ButtonVariant = 'primary' | 'secondary' | 'link' | 'bold-link'

export interface CtaCardProps {
  title: string
  subtitle?: string
  illustration?: string | string[]
  illustrationColors?: string | string[]
  linkText?: string
  linkHref?: string
  linkOnClick?: () => void
  linkAsButton?: boolean
  buttonVariant?: ButtonVariant
  border?: boolean
  style?: Style
  onClick?: () => void
}

export const CtaCard = ({
  title,
  subtitle,
  illustration,
  illustrationColors,
  linkText,
  linkHref,
  linkOnClick,
  linkAsButton = false,
  buttonVariant = 'primary',
  border = false,
  style,
  onClick,
}: CtaCardProps) => {
  const [, setLocation] = useLocation()
  const illustrations = Array.isArray(illustration)
    ? illustration
    : illustration
      ? [illustration]
      : []
  const colors = Array.isArray(illustrationColors)
    ? illustrationColors
    : illustrationColors
      ? [illustrationColors]
      : []
  const hasMultipleIllustrations = illustrations.length > 1
  return (
    <styled.div className="a a8 b c e t fe"
      data-testid="cta-card"
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : 'default',
        border: border ? 'var(--gray-border)' : 'none',
        borderRadius: border ? '20px' : '0',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              opacity: 0.9,
            }
          : {},
        ...style,
      }}
    >
      {/* Illustrations */}
      {illustrations.length > 0 && (
        <div className="ay a c h a8 r"
        >
          {hasMultipleIllustrations ? (
            <Row
              gap="1.5rem"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth: '100%',
              }}
            >
              {illustrations.map((img, index) => (
                <Illustration
                  key={index}
                  src={img}
                  alt={`${title} illustration ${index + 1}`}
                  color={colors[index]}
                  width="5rem"
                  height="5rem"
                />
              ))}
            </Row>
          ) : (
            <Illustration
              src={illustrations[0]}
              alt={`${title} illustration`}
              color={colors[0]}
              width="200px"
              height="200px"
            />
          )}
        </div>
      )}

      {/* Content */}
      <Column
        alignItems="center"
        gap="1rem"
        style={{
          maxWidth: '400px',
        }}
      >
        {/* Title */}
        <Title.H3
          style={{
            color: 'var(--primary-blue)',
            margin: 0,
            fontSize: '1.75rem',
            fontWeight: '700',
            textAlign: 'center',
          }}
        >
          {title}
        </Title.H3>

        {/* Subtitle */}
        {subtitle && (
          <Text.Medium
            style={{
              color: 'var(--primary-blue)',
              margin: 0,
              lineHeight: '1.6',
              textAlign: 'center',
              opacity: 0.8,
            }}
          >
            {subtitle}
          </Text.Medium>
        )}

        {/* Link or Button */}
        {linkText && (linkHref || linkOnClick) && (
          <div className="ff">
            {linkAsButton ? (
              <Button
                variant={buttonVariant}
                label={linkText}
                onClick={
                  linkOnClick || (() => linkHref && setLocation(linkHref))
                }
              />
            ) : linkHref ? (
              <TextLink to={linkHref} hasIcon={true} iconPosition="right">
                {linkText}
              </TextLink>
            ) : (
              <button className="bq b3 b8 aj fg br b5 fh"
                onClick={linkOnClick}
              >
                {linkText}
              </button>
            )}
          </div>
        )}
      </Column>
    </styled.div>
  )
}

// Illustration component that handles SVG coloring
interface IllustrationProps {
  src: string
  alt: string
  color?: string
  width: string
  height: string
}

const Illustration = ({
  src,
  alt,
  color,
  width,
  height,
}: IllustrationProps) => {
  const [svgContent, setSvgContent] = useState<string | null>(null)
  const isSvg = src.endsWith('.svg')

  useEffect(() => {
    // Only fetch SVG content if we need to apply color
    if (isSvg && color) {
      fetch(src)
        .then((response) => response.text())
        .then((svgText) => {
          // Apply color to the SVG by replacing fill attributes or adding a style
          const coloredSvg = svgText
            .replace(/<svg/, `<svg style="width: 100%; height: 100%;"`)
            .replace(/fill="[^"]*"/g, `fill="${color}"`)
            .replace(/<path/g, `<path fill="${color}"`)
            .replace(/<circle/g, `<circle fill="${color}"`)
            .replace(/<rect/g, `<rect fill="${color}"`)
            .replace(/<polygon/g, `<polygon fill="${color}"`)
          setSvgContent(coloredSvg)
        })
        .catch(() => {
          // Fallback to regular img if fetch fails
          setSvgContent(null)
        })
    }
  }, [src, color, isSvg])

  // If SVG with color and we have the content, render inline
  if (isSvg && color && svgContent) {
    return (
      <styled.div className="c6 a c h"
        style={{
          width,
          height,}}
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    )
  }

  // Default: render as img tag
  return (
    <styled.div className="c6 a c h c0"
      style={{
        width,
        height,}}
    >
      <Image
        src={src}
        alt={alt}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      />
    </styled.div>
  )
}
